"use client";

// src/ext/router-provider.tsx
import { createContext, useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
var RouterContext = createContext({
  isNative: true,
  open: openSyntheticLink,
  useHref: (href) => href
});
var supportsPreventScrollCached = null;
function supportsPreventScroll() {
  if (supportsPreventScrollCached == null) {
    supportsPreventScrollCached = false;
    try {
      let focusElem = document.createElement("div");
      focusElem.focus({
        get preventScroll() {
          supportsPreventScrollCached = true;
          return true;
        }
      });
    } catch {
    }
  }
  return supportsPreventScrollCached;
}
function focusWithoutScrolling(element) {
  if (supportsPreventScroll()) {
    element.focus({ preventScroll: true });
  } else {
    let scrollableElements = getScrollableElements(element);
    element.focus();
    restoreScrollPosition(scrollableElements);
  }
}
function getScrollableElements(element) {
  let parent = element.parentNode;
  let scrollableElements = [];
  let rootScrollingElement = document.scrollingElement || document.documentElement;
  while (parent instanceof HTMLElement && parent !== rootScrollingElement) {
    if (parent.offsetHeight < parent.scrollHeight || parent.offsetWidth < parent.scrollWidth) {
      scrollableElements.push({
        element: parent,
        scrollTop: parent.scrollTop,
        scrollLeft: parent.scrollLeft
      });
    }
    parent = parent.parentNode;
  }
  if (rootScrollingElement instanceof HTMLElement) {
    scrollableElements.push({
      element: rootScrollingElement,
      scrollTop: rootScrollingElement.scrollTop,
      scrollLeft: rootScrollingElement.scrollLeft
    });
  }
  return scrollableElements;
}
function restoreScrollPosition(scrollableElements) {
  for (let { element, scrollTop, scrollLeft } of scrollableElements) {
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  }
}
function cached(fn) {
  if (process.env.NODE_ENV === "test") {
    return fn;
  }
  let res = null;
  return () => {
    if (res == null) {
      res = fn();
    }
    return res;
  };
}
function testUserAgent(re) {
  var _a;
  if (typeof window === "undefined" || window.navigator == null) {
    return false;
  }
  return ((_a = window.navigator["userAgentData"]) == null ? void 0 : _a.brands.some(
    (brand) => re.test(brand.brand)
  )) || re.test(window.navigator.userAgent);
}
function testPlatform(re) {
  var _a;
  return typeof window !== "undefined" && window.navigator != null ? re.test(((_a = window.navigator["userAgentData"]) == null ? void 0 : _a.platform) || window.navigator.platform) : false;
}
var isChrome = cached(function() {
  return testUserAgent(/Chrome/i);
});
var isWebKit = cached(function() {
  return testUserAgent(/AppleWebKit/i) && !isChrome();
});
var isMac = cached(function() {
  return testPlatform(/^Mac/i);
});
var isFirefox = cached(function() {
  return testUserAgent(/Firefox/i);
});
var isIPad = cached(function() {
  return testPlatform(/^iPad/i) || isMac() && navigator.maxTouchPoints > 1;
});
function RouterProvider(props) {
  let { children, navigate, useHref } = props;
  let ctx = useMemo(
    () => ({
      isNative: false,
      open: (target, modifiers, href, routerOptions) => {
        getSyntheticLink(target, (link) => {
          if (shouldClientNavigate(link, modifiers)) {
            navigate(href, routerOptions);
          } else {
            openLink(link, modifiers);
          }
        });
      },
      useHref: useHref || ((href) => href)
    }),
    [navigate, useHref]
  );
  return /* @__PURE__ */ jsx(RouterContext.Provider, { value: ctx, children });
}
function shouldClientNavigate(link, modifiers) {
  let target = link.getAttribute("target");
  return (!target || target === "_self") && link.origin === location.origin && !link.hasAttribute("download") && !modifiers.metaKey && !modifiers.ctrlKey && !modifiers.altKey && !modifiers.shiftKey;
}
function useRouter() {
  return useContext(RouterContext);
}
function openLink(target, modifiers, setOpening = true) {
  var _a, _b;
  let { metaKey, ctrlKey, altKey, shiftKey } = modifiers;
  if (isFirefox() && ((_b = (_a = window.event) == null ? void 0 : _a.type) == null ? void 0 : _b.startsWith("key")) && target.target === "_blank") {
    if (isMac()) {
      metaKey = true;
    } else {
      ctrlKey = true;
    }
  }
  let event = isWebKit() && isMac() && !isIPad() && process.env.NODE_ENV !== "test" ? (
    // @ts-ignore
    new KeyboardEvent("keydown", { keyIdentifier: "Enter", metaKey, ctrlKey, altKey, shiftKey })
  ) : new MouseEvent("click", {
    metaKey,
    ctrlKey,
    altKey,
    shiftKey,
    bubbles: true,
    cancelable: true
  });
  openLink.isOpening = setOpening;
  focusWithoutScrolling(target);
  target.dispatchEvent(event);
  openLink.isOpening = false;
}
openLink.isOpening = false;
function getSyntheticLink(target, open) {
  if (target instanceof HTMLAnchorElement) {
    open(target);
  } else if (target.hasAttribute("data-href")) {
    let link = document.createElement("a");
    link.href = target.getAttribute("data-href");
    if (target.hasAttribute("data-target")) {
      link.target = target.getAttribute("data-target");
    }
    if (target.hasAttribute("data-rel")) {
      link.rel = target.getAttribute("data-rel");
    }
    if (target.hasAttribute("data-download")) {
      link.download = target.getAttribute("data-download");
    }
    if (target.hasAttribute("data-ping")) {
      link.ping = target.getAttribute("data-ping");
    }
    if (target.hasAttribute("data-referrer-policy")) {
      link.referrerPolicy = target.getAttribute("data-referrer-policy");
    }
    target.appendChild(link);
    open(link);
    target.removeChild(link);
  }
}
function openSyntheticLink(target, modifiers) {
  getSyntheticLink(target, (link) => openLink(link, modifiers));
}

export {
  RouterProvider,
  shouldClientNavigate,
  useRouter
};
